import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import type { MouseEvent, MouseEventHandler } from 'react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { VALID_IMAGE_LAYOUTS, IMAGE_LOADING } from './constants'
import { LAYOUT_RESPONSIVE, LAYOUT_COVER, LAYOUT_CONTAIN } from './constants'
import styles from './index.module.css'
import type { Image as ImageAggregate } from '@s4l/stayforlong/kernel/Image'
import { useS4LContext } from '@s4l/react-context'
import { useLazyLoading } from '@s4l/utils/hooks/useLazyLoading'
import { imagePreset } from './imagePreset'
import { Placeholder } from '@s4l/ds'
import Picture from './Picture'

interface IImageProps {
  alt: string
  src: string
  layout?: (typeof VALID_IMAGE_LAYOUTS)[number]
  onClick?: MouseEventHandler
  onError?: () => void
  width?: string | number
  height?: string | number
  preload?: boolean
  loading?: (typeof IMAGE_LOADING)[number]
  aspectRatio?: string
  style?: React.CSSProperties
  className?: string
  dataTestId?: string
  srcSet?: string[]
  sizes?: string[]
}

interface IImageModelProps {
  image: ImageAggregate
  preset: keyof typeof imagePreset
  alt: string
  loading?: (typeof IMAGE_LOADING)[number]
}

function Image(props: IImageProps) {
  const {
    alt,
    src,
    width,
    height,
    layout = LAYOUT_COVER,
    onClick,
    onError,
    loading = 'eager',
    aspectRatio = '',
    style = {},
    className,
    dataTestId,
    srcSet = [],
    sizes = [],
  } = props

  const ref = useRef(null)
  const inViewport = useLazyLoading(ref)

  const lazyLoad = loading === 'lazy'

  const imageDimensions = layout === LAYOUT_RESPONSIVE ? undefined : { width, height }
  const imageSrcSet = srcSet.length > 0 ? `${srcSet.join(', ')}` : undefined
  const imageSizes = sizes.length > 0 ? `${sizes.join(', ')}` : undefined

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    if (onClick) onClick(event)
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className={`${styles.imageBase} ${className || ''}`}
      onClick={handleClick}
      data-testid={dataTestId}
      ref={ref}
    >
      {lazyLoad && !inViewport ? (
        <Placeholder
          width={typeof width === 'number' ? `${width}px` : width}
          height={typeof height === 'number' ? `${height}px` : height}
        />
      ) : (
        <img
          src={src}
          alt={alt}
          {...imageDimensions}
          srcSet={imageSrcSet}
          sizes={imageSizes}
          {...(loading === 'eager' ? {} : { loading })}
          style={{
            ...style,
            ...(layout === LAYOUT_RESPONSIVE && { width: '100%' }),
            ...(layout === LAYOUT_CONTAIN && { objectFit: 'contain' }),
            ...(layout === LAYOUT_COVER && { objectFit: 'cover' }),
            ...(aspectRatio ? { aspectRatio } : {}),
          }}
          onError={() => onError?.()}
        />
      )}
    </div>
  )
}

const useBreakpoints = () => {
  const [breakpoints, setBreakpoints] = useState<Record<string, string> | null>(null)

  useEffect(() => {
    const computedStyle = getComputedStyle(document.documentElement)
    setBreakpoints({
      tabletBreakpoint: computedStyle.getPropertyValue('--tablet-breakpoint').trim(),
      desktopBreakpoint: computedStyle.getPropertyValue('--desktop-breakpoint').trim(),
    })
  }, [])

  return breakpoints
}

export const renderSizes = (
  sizes: readonly number[],
  breakpoints: Record<string, string>
): string[] => {
  const tabletBreakpoint = parseInt(breakpoints.tabletBreakpoint, 10)
  const desktopBreakpoint = parseInt(breakpoints.desktopBreakpoint, 10)

  const sizesAttr: string[] = []

  if (sizes?.[2]) {
    sizesAttr.push(`(min-width: ${desktopBreakpoint}px) ${sizes[2]}px`)
  }

  if (sizes?.[1]) {
    sizesAttr.push(
      `(min-width: ${tabletBreakpoint}px) and (max-width: ${desktopBreakpoint - 1}px) ${sizes[1]}px`
    )
  }

  sizesAttr.push(`${sizes[0]}px`)

  return sizesAttr
}

function ImageModel({ image, preset, alt, loading = 'lazy' }: IImageModelProps) {
  const { domain } = useS4LContext()
  const presetConfig = imagePreset[preset]

  const breakpoints = useBreakpoints()

  const sizes = useMemo(
    () => (breakpoints ? renderSizes(presetConfig.sizes, breakpoints) : []),
    [presetConfig.sizes, breakpoints]
  )

  const srcSet = useMemo(() => {
    const config = domain.config()

    // Generar todos los anchos (x1, x2, x3) y filtrar duplicados
    const allWidths = presetConfig.srcWidth.flatMap(width => [width, width * 2, width * 3])

    const uniqueWidths = [...new Set(allWidths)].sort((a, b) => a - b)

    return uniqueWidths.map(width => `${image.toUrl({ config, width })} ${width}w`)
  }, [image, domain, presetConfig.srcWidth])

  return (
    <Image
      key={image.toBase64()}
      alt={alt}
      src={image.toUrl({ config: domain.config(), width: presetConfig.sizes[0] })}
      srcSet={srcSet}
      sizes={sizes}
      loading={loading}
    />
  )
}

Image.Model = ImageModel
Image.Picture = Picture

Image.displayName = 'Image'
export default withErrorBoundary(Image)
